import type { AxiosInstance } from 'axios'
import type { H3Event } from 'h3'
import type { ComputedRef } from 'vue'
import type { NuxtApp } from '#app'
import type { ProPartnersNormalized, ProPartnersPayload } from '~/utils/proPartners'
import {
  cloneProPartners,
  DEFAULT_PRO_PARTNERS_FALLBACK_FILE,
  EMPTY_PRO_PARTNERS,
  mergeProPartners,
} from '~/utils/proPartners'
import { loadFallbackJsonPayload } from '~/utils/helpers'
import { CachedPageUtils } from '~/utils/CachedPageUtils'

export type UseProPartnersDataReturn = {
  proPartners: ComputedRef<ProPartnersNormalized>
}

/**
 * @param {AxiosInstance} api - Client HTTP API.
 * @param {string} fallbackFile - Fichier ou URL fallback (`NUXT_PUBLIC_PRO_PARTNERS_FALLBACK_URL`).
 * @param {string} requestOrigin - Origin SSR / client pour le fetch static.
 * @param {H3Event | undefined} requestEvent - Requête en cours côté serveur, absente dans le navigateur.
 * @returns {Promise<ProPartnersNormalized>} Partenaires fusionnés JSON + API.
 */
async function fetchProPartnersMerged(
  api: AxiosInstance,
  fallbackFile: string,
  requestOrigin: string,
  requestEvent: H3Event | undefined,
): Promise<ProPartnersNormalized> {
  const jsonPayload: ProPartnersPayload | null = await loadFallbackJsonPayload<ProPartnersPayload>(
    fallbackFile,
    DEFAULT_PRO_PARTNERS_FALLBACK_FILE,
    requestOrigin,
  )

  let apiPayload: ProPartnersPayload | null = null
  try {
    const { data } = await api.get<ProPartnersPayload>('/api/pro-partners', { timeout: 8000 })
    apiPayload = data ?? null
  } catch (error: unknown) {
    CachedPageUtils.reportFailedApiCall('/api/pro-partners', error, requestEvent)
    apiPayload = null
  }

  const fromJson: ProPartnersNormalized = mergeProPartners(
    cloneProPartners(EMPTY_PRO_PARTNERS),
    jsonPayload,
  )
  return mergeProPartners(fromJson, apiPayload)
}

/**
 * Logos partenaires Pro — JSON fallback (`public/`) puis patch API.
 * @returns {UseProPartnersDataReturn} `proPartners` calculé depuis JSON + patch API.
 */
export function useProPartnersData(): UseProPartnersDataReturn {
  const config: ReturnType<typeof useRuntimeConfig> = useRuntimeConfig()
  const api: AxiosInstance = useFrontApi()
  const proPartnersFallbackFile: string = config.public.proPartnersFallbackFile
  const requestOrigin: string = useRequestURL().origin
  const requestEvent: H3Event | undefined = useRequestEvent()

  const { data: proPartnersMerged } = useAsyncData<ProPartnersNormalized>(
    'pro-partners',
    (): Promise<ProPartnersNormalized> =>
      fetchProPartnersMerged(api, proPartnersFallbackFile, requestOrigin, requestEvent),
    {
      server: true,
      lazy: false,
      default: () => cloneProPartners(EMPTY_PRO_PARTNERS),
      getCachedData(key: string, nuxtApp: NuxtApp): ProPartnersNormalized | undefined {
        const cached: ProPartnersNormalized | undefined = nuxtApp.payload.data[key] as
          | ProPartnersNormalized
          | undefined
        if (import.meta.client && cached && cached.length === 0) {
          return undefined
        }
        return cached
      },
    },
  )

  const proPartners: ComputedRef<ProPartnersNormalized> = computed(
    (): ProPartnersNormalized => proPartnersMerged.value ?? cloneProPartners(EMPTY_PRO_PARTNERS),
  )

  return { proPartners }
}
